
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../hooks/useLanguage';

function FooterAccordion({
  title,
  icon,
  children,
}: {
  title: string;
  icon: string;
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="md:hidden border-b border-slate-700/50">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-4 cursor-pointer"
      >
        <span className="text-base font-semibold flex items-center gap-2">
          <i className={`${icon} text-teal-400`}></i>
          {title}
        </span>
        <i
          className={`ri-arrow-${isOpen ? 'up' : 'down'}-s-line text-slate-400 text-lg transition-transform`}
        ></i>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-96 pb-4' : 'max-h-0'
        }`}
      >
        {children}
      </div>
    </div>
  );
}

export default function Footer() {
  const { t } = useLanguage();

  return (
    <footer className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white pt-10 md:pt-16 pb-6 md:pb-8">
      <div className="container mx-auto px-4">
        {/* Trust Badges - Mobile */}
        <div className="grid grid-cols-2 gap-3 mb-8 md:hidden">
          <div className="bg-slate-800/60 rounded-xl p-3 flex items-center gap-2.5">
            <span className="w-8 h-8 flex items-center justify-center bg-teal-500/20 rounded-lg flex-shrink-0">
              <i className="ri-shield-check-line text-teal-400 text-base"></i>
            </span>
            <span className="text-xs text-slate-300 leading-tight">
              {t('footer.original_licenses')}
            </span>
          </div>
          <div className="bg-slate-800/60 rounded-xl p-3 flex items-center gap-2.5">
            <span className="w-8 h-8 flex items-center justify-center bg-teal-500/20 rounded-lg flex-shrink-0">
              <i className="ri-flashlight-line text-teal-400 text-base"></i>
            </span>
            <span className="text-xs text-slate-300 leading-tight">
              {t('footer.instant_delivery')}
            </span>
          </div>
          <div className="bg-slate-800/60 rounded-xl p-3 flex items-center gap-2.5">
            <span className="w-8 h-8 flex items-center justify-center bg-teal-500/20 rounded-lg flex-shrink-0">
              <i className="ri-lock-line text-teal-400 text-base"></i>
            </span>
            <span className="text-xs text-slate-300 leading-tight">
              {t('footer.secure_payment')}
            </span>
          </div>
          <div className="bg-slate-800/60 rounded-xl p-3 flex items-center gap-2.5">
            <span className="w-8 h-8 flex items-center justify-center bg-teal-500/20 rounded-lg flex-shrink-0">
              <i className="ri-customer-service-2-line text-teal-400 text-base"></i>
            </span>
            <span className="text-xs text-slate-300 leading-tight">
              {t('footer.support_7days')}
            </span>
          </div>
        </div>

        {/* Desktop Grid */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Column 1: Logo & description */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-lg flex items-center justify-center">
                <i className="ri-shield-keyhole-line text-xl text-white"></i>
              </div>
              <span className="text-xl font-bold">Digital Licence Store</span>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              {t('footer.trusted_store')}
            </p>
          </div>

          {/* Column 2: Quick links */}
          <div>
            <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <i className="ri-links-line text-teal-400"></i>
              <a href="#enlaces-rapidos">{t('footer.quick_links')}</a>
            </h4>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/"
                  className="text-slate-300 hover:text-teal-400 transition-colors text-sm flex items-center gap-2 group"
                >
                  <i className="ri-home-line text-xs group-hover:translate-x-1 transition-transform"></i>
                  {t('footer.home')}
                </Link>
              </li>
              <li>
                <Link
                  to="/windows"
                  className="text-slate-300 hover:text-teal-400 transition-colors text-sm flex items-center gap-2 group"
                >
                  <i className="ri-windows-line text-xs group-hover:translate-x-1 transition-transform"></i>
                  Windows
                </Link>
              </li>
              <li>
                <Link
                  to="/office"
                  className="text-slate-300 hover:text-teal-400 transition-colors text-sm flex items-center gap-2 group"
                >
                  <i className="ri-file-word-line text-xs group-hover:translate-x-1 transition-transform"></i>
                  Office
                </Link>
              </li>
              <li>
                <Link
                  to="/antivirus"
                  className="text-slate-300 hover:text-teal-400 transition-colors text-sm flex items-center gap-2 group"
                >
                  <i className="ri-shield-check-line text-xs group-hover:translate-x-1 transition-transform"></i>
                  {t('nav.antivirus')}
                </Link>
              </li>
              <li>
                <Link
                  to="/vpn"
                  className="text-slate-300 hover:text-teal-400 transition-colors text-sm flex items-center gap-2 group"
                >
                  <i className="ri-shield-line text-xs group-hover:translate-x-1 transition-transform"></i>
                  VPN
                </Link>
              </li>
              <li>
                <Link
                  to="/distribuidor"
                  className="text-slate-300 hover:text-teal-400 transition-colors text-sm flex items-center gap-2 group"
                >
                  <i className="ri-store-line text-xs group-hover:translate-x-1 transition-transform"></i>
                  {t('footer.distributors')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 3: Support */}
          <div>
            <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <i className="ri-customer-service-2-line text-teal-400"></i>
              <a href="#soporte">{t('footer.support')}</a>
            </h4>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/testimonios"
                  className="text-slate-300 hover:text-teal-400 transition-colors text-sm flex items-center gap-2 group"
                >
                  <i className="ri-star-line text-xs group-hover:translate-x-1 transition-transform"></i>
                  {t('footer.testimonials')}
                </Link>
              </li>
              <li>
                <a
                  href="mailto:DigitalLicenceStore@offersshopping.com"
                  rel="nofollow"
                  className="text-slate-300 hover:text-teal-400 transition-colors text-sm flex items-center gap-2 group"
                >
                  <i className="ri-mail-line text-xs group-hover:translate-x-1 transition-transform"></i>
                  DigitalLicenceStore@offersshopping.com
                </a>
              </li>
              <li>
                <a
                  href="https://wa.me/79534940353"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="text-slate-300 hover:text-teal-400 transition-colors text-sm flex items-center gap-2 group"
                >
                  <i className="ri-whatsapp-line text-xs group-hover:translate-x-1 transition-transform"></i>
                  WhatsApp
                </a>
              </li>
              <li>
                <a
                  href="https://t.me/Sup0rteWin"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="text-slate-300 hover:text-teal-400 transition-colors text-sm flex items-center gap-2 group"
                >
                  <i className="ri-telegram-line text-xs group-hover:translate-x-1 transition-transform"></i>
                  Telegram
                </a>
              </li>
              <li>
                <Link
                  to="/legal"
                  className="text-slate-300 hover:text-teal-400 transition-colors text-sm flex items-center gap-2 group"
                >
                  <i className="ri-file-shield-2-line text-xs group-hover:translate-x-1 transition-transform"></i>
                  {t('footer.privacy_policy')}
                </Link>
              </li>
              <li>
                <Link
                  to="/legal#terminos"
                  className="text-slate-300 hover:text-teal-400 transition-colors text-sm flex items-center gap-2 group"
                >
                  <i className="ri-file-text-line text-xs group-hover:translate-x-1 transition-transform"></i>
                  {t('footer.terms')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Column 4: Contact */}
          <div>
            <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <i className="ri-customer-service-line text-teal-400"></i>
              <a href="#contacto">{t('footer.contact')}</a>
            </h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3 group">
                <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <i className="ri-whatsapp-line text-sm text-white"></i>
                </div>
                <div>
                  <p className="text-xs text-slate-400 mb-1">WhatsApp</p>
                  <a
                    href="https://wa.me/79534940353"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="text-slate-200 hover:text-teal-400 transition-colors text-sm font-medium"
                  >
                    +7 953 494 0353
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3 group">
                <div className="w-8 h-8 bg-gradient-to-br from-rose-500 to-pink-600 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <i className="ri-mail-line text-sm text-white"></i>
                </div>
                <div>
                  <p className="text-xs text-slate-400 mb-1">Email</p>
                  <a
                    href="mailto:DigitalLicenceStore@offersshopping.com"
                    rel="nofollow"
                    className="text-slate-200 hover:text-teal-400 transition-colors text-sm font-medium break-all"
                  >
                    DigitalLicenceStore@offersshopping.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3 group">
                <div className="w-8 h-8 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <i className="ri-time-line text-sm text-white"></i>
                </div>
                <div>
                  <p className="text-xs text-slate-400 mb-1">{t('footer.schedule')}</p>
                  <p className="text-slate-200 text-sm font-medium">{t('footer.mon_sun')}</p>
                  <p className="text-teal-400 text-sm font-semibold">7:00 AM - 19:00 PM</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Accordion Sections */}
        <div className="md:hidden mb-6">
          {/* Mobile logo */}
          <div className="flex items-center gap-2.5 mb-5 pb-5 border-b border-slate-700/50">
            <div className="w-9 h-9 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-lg flex items-center justify-center">
              <i className="ri-shield-keyhole-line text-lg text-white"></i>
            </div>
            <div>
              <span className="text-base font-bold block">Digital Licence Store</span>
              <span className="text-[10px] text-teal-400 italic">✦ {t('footer.slogan')}</span>
            </div>
          </div>

          <FooterAccordion title={t('footer.quick_links')} icon="ri-links-line">
            <div className="grid grid-cols-2 gap-2">
              <Link
                to="/"
                className="text-slate-300 hover:text-teal-400 transition-colors text-sm flex items-center gap-2 py-1.5"
              >
                <i className="ri-home-line text-xs text-teal-400/60"></i>
                {t('footer.home')}
              </Link>
              <Link
                to="/windows"
                className="text-slate-300 hover:text-teal-400 transition-colors text-sm flex items-center gap-2 py-1.5"
              >
                <i className="ri-windows-line text-xs text-teal-400/60"></i>
                Windows
              </Link>
              <Link
                to="/office"
                className="text-slate-300 hover:text-teal-400 transition-colors text-sm flex items-center gap-2 py-1.5"
              >
                <i className="ri-file-word-line text-xs text-teal-400/60"></i>
                Office
              </Link>
              <Link
                to="/antivirus"
                className="text-slate-300 hover:text-teal-400 transition-colors text-sm flex items-center gap-2 py-1.5"
              >
                <i className="ri-shield-check-line text-xs text-teal-400/60"></i>
                {t('nav.antivirus')}
              </Link>
              <Link
                to="/vpn"
                className="text-slate-300 hover:text-teal-400 transition-colors text-sm flex items-center gap-2 py-1.5"
              >
                <i className="ri-shield-line text-xs text-teal-400/60"></i>
                VPN
              </Link>
              <Link
                to="/distribuidor"
                className="text-slate-300 hover:text-teal-400 transition-colors text-sm flex items-center gap-2 py-1.5"
              >
                <i className="ri-store-line text-xs text-teal-400/60"></i>
                {t('footer.distributors')}
              </Link>
            </div>
          </FooterAccordion>

          <FooterAccordion title={t('footer.contact')} icon="ri-customer-service-line">
            <div className="space-y-3">
              <a
                href="https://wa.me/79534940353"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex items-center gap-3 bg-slate-800/50 rounded-xl p-3 hover:bg-slate-700/50 transition-colors cursor-pointer"
              >
                <div className="w-9 h-9 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-whatsapp-line text-base text-white"></i>
                </div>
                <div>
                  <p className="text-xs text-slate-400">WhatsApp</p>
                  <p className="text-sm text-slate-200 font-medium">+7 953 494 0353</p>
                </div>
              </a>
              <a
                href="https://t.me/Sup0rteWin"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex items-center gap-3 bg-slate-800/50 rounded-xl p-3 hover:bg-slate-700/50 transition-colors cursor-pointer"
              >
                <div className="w-9 h-9 bg-gradient-to-br from-sky-500 to-sky-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-telegram-line text-base text-white"></i>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Telegram</p>
                  <p className="text-sm text-slate-200 font-medium">@Sup0rteWin</p>
                </div>
              </a>
              <a
                href="mailto:DigitalLicenceStore@offersshopping.com"
                rel="nofollow"
                className="flex items-center gap-3 bg-slate-800/50 rounded-xl p-3 hover:bg-slate-700/50 transition-colors cursor-pointer"
              >
                <div className="w-9 h-9 bg-gradient-to-br from-rose-500 to-pink-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-base text-white"></i>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Email</p>
                  <p className="text-sm text-slate-200 font-medium break-all">
                    DigitalLicenceStore@offersshopping.com
                  </p>
                </div>
              </a>
              <div className="flex items-center gap-3 bg-slate-800/50 rounded-xl p-3">
                <div className="w-9 h-9 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-base text-white"></i>
                </div>
                <div>
                  <p className="text-xs text-slate-400">{t('footer.schedule_short')}</p>
                  <p className="text-sm text-slate-200 font-medium">
                    {t('footer.mon_sun_short')} ·{' '}
                    <span className="text-teal-400">7:00 - 19:00</span>
                  </p>
                </div>
              </div>
            </div>
          </FooterAccordion>
        </div>

        {/* Payment Methods */}
        <div className="border-t border-slate-700/50 pt-6 mb-6">
          <p className="text-center text-xs text-slate-500 mb-3">{t('footer.payment_methods')}</p>
          <div className="flex items-center justify-center gap-4 sm:gap-6 flex-wrap">
            <div className="flex items-center gap-1.5 text-slate-400">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-paypal-line text-base"></i>
              </span>
              <span className="text-xs">PayPal</span>
            </div>
            <div className="flex items-center gap-1.5 text-slate-400">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-bank-card-line text-base"></i>
              </span>
              <span className="text-xs">MercadoPago</span>
            </div>
            <div className="flex items-center gap-1.5 text-slate-400">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-exchange-dollar-line text-base"></i>
              </span>
              <span className="text-xs">{t('footer.transfer')}</span>
            </div>
            <div className="flex items-center gap-1.5 text-slate-400">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-bitcoin-line text-base"></i>
              </span>
              <span className="text-xs">Crypto</span>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-700 pt-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-slate-400 text-xs text-center sm:text-left">
              © 2024 Digital Licence Store. {t('footer.all_rights')}
            </p>
            <div className="flex items-center gap-5">
              <a
                href="https://wa.me/79534940353"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="text-slate-400 hover:text-teal-400 transition-colors cursor-pointer"
              >
                <i className="ri-whatsapp-line text-lg"></i>
              </a>
              <a
                href="https://t.me/Sup0rteWin"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="text-slate-400 hover:text-teal-400 transition-colors cursor-pointer"
              >
                <i className="ri-telegram-line text-lg"></i>
              </a>
              <a
                href="mailto:DigitalLicenceStore@offersshopping.com"
                rel="nofollow"
                className="text-slate-400 hover:text-teal-400 transition-colors cursor-pointer"
              >
                <i className="ri-mail-line text-lg"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
